// src/pages/ForgotPassword.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/customButtons/Button';
import { useAuth } from '../context/AuthContext';

const ForgotPassword = () => {
    const { requestPasswordReset } = useAuth();
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [attempts, setAttempts] = useState(0);
    const [cooldown, setCooldown] = useState(0);
    const [lastAttemptTime, setLastAttemptTime] = useState(0);
    const [emailSuggestions, setEmailSuggestions] = useState([]);
    const [typingTimer, setTypingTimer] = useState(null);

    // Load attempts from localStorage on component mount
    useEffect(() => {
        const storedAttempts = localStorage.getItem('passwordResetAttempts');
        const storedLastAttempt = localStorage.getItem('passwordResetLastAttempt');

        if (storedAttempts) {
            setAttempts(parseInt(storedAttempts));
        }
        if (storedLastAttempt) {
            setLastAttemptTime(parseInt(storedLastAttempt));
        }
    }, []);

    // Handle cooldown timer
    useEffect(() => {
        let interval;
        if (cooldown > 0) {
            interval = setInterval(() => {
                setCooldown(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [cooldown]);

    // Common email domain suggestions
    const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com'];
    const commonTypos = {
        'gmial.com': 'gmail.com',
        'gmail.cmo': 'gmail.com',
        'gmail.con': 'gmail.com',
        'gmil.com': 'gmail.com',
        'yaho.com': 'yahoo.com',
        'yahoo.cmo': 'yahoo.com',
        'hotmail.cmo': 'hotmail.com',
        'hotmial.com': 'hotmail.com',
        'outlook.cmo': 'outlook.com'
    };

    // Enhanced email validation with suggestions
    const validateEmail = (email) => {
        if (!email) return { isValid: false, error: 'Email address is required' };

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return { isValid: false, error: 'Please enter a valid email address' };
        }

        if (email.length > 254) {
            return { isValid: false, error: 'Email address is too long' };
        }

        // Check for disposable email domains
        const disposableDomains = [
            'tempmail.com', 'guerrillamail.com', 'mailinator.com', '10minutemail.com',
            'throwaway.com', 'fakeinbox.com', 'yopmail.com', 'trashmail.com',
            'temp-mail.org', 'fake-mail.com', 'disposableemail.com'
        ];
        const domain = email.split('@')[1];
        if (disposableDomains.includes(domain)) {
            return { isValid: false, error: 'Disposable email addresses are not allowed' };
        }

        // Check for common typos and provide suggestions
        const suggestions = [];
        const [localPart, currentDomain] = email.split('@');

        // Check for domain typos
        if (commonTypos[currentDomain]) {
            suggestions.push(`${localPart}@${commonTypos[currentDomain]}`);
        }

        // Suggest common domains if current domain is incomplete
        if (currentDomain && currentDomain.length < 3) {
            commonDomains.forEach(domain => {
                if (domain.startsWith(currentDomain)) {
                    suggestions.push(`${localPart}@${domain}`);
                }
            });
        }

        // Suggest common domains if no domain match
        if (!commonDomains.includes(currentDomain) && !suggestions.length) {
            commonDomains.forEach(domain => {
                suggestions.push(`${localPart}@${domain}`);
            });
        }

        setEmailSuggestions(suggestions.slice(0, 3)); // Show max 3 suggestions

        return { isValid: true, error: '', suggestions };
    };

    // Check email format and common issues
    const checkEmailIssues = (email) => {
        const issues = [];

        if (!email.includes('@')) {
            issues.push('Missing @ symbol');
            return issues;
        }

        const [localPart, domain] = email.split('@');

        if (!localPart) {
            issues.push('Missing email username');
        }

        if (!domain) {
            issues.push('Missing domain name');
        }

        if (domain && !domain.includes('.')) {
            issues.push('Domain missing extension (e.g., .com)');
        }

        if (email.includes(' ')) {
            issues.push('Email should not contain spaces');
        }

        if (email !== email.toLowerCase()) {
            issues.push('Email should be in lowercase');
        }

        return issues;
    };

    // Check if user is rate limited
    const isRateLimited = () => {
        const now = Date.now();
        const timeSinceLastAttempt = now - lastAttemptTime;

        // Reset attempts if it's been more than 1 hour
        if (timeSinceLastAttempt > 60 * 60 * 1000) {
            setAttempts(0);
            localStorage.removeItem('passwordResetAttempts');
            localStorage.removeItem('passwordResetLastAttempt');
            return false;
        }

        // Rate limiting rules
        if (attempts >= 5) {
            const cooldownTime = Math.ceil((60 * 60 * 1000 - timeSinceLastAttempt) / 1000 / 60);
            setCooldown(cooldownTime);
            return true;
        }

        return false;
    };

    // Update attempt counter
    const updateAttempts = () => {
        const newAttempts = attempts + 1;
        const now = Date.now();

        setAttempts(newAttempts);
        setLastAttemptTime(now);

        localStorage.setItem('passwordResetAttempts', newAttempts.toString());
        localStorage.setItem('passwordResetLastAttempt', now.toString());

        // Apply cooldown after 3 attempts
        if (newAttempts >= 3) {
            setCooldown(5); // 5 minute cooldown
        }
    };

    // Reset attempt counter on success
    const resetAttempts = () => {
        setAttempts(0);
        setCooldown(0);
        localStorage.removeItem('passwordResetAttempts');
        localStorage.removeItem('passwordResetLastAttempt');
    };

    const handleEmailChange = (value) => {
        const sanitizedEmail = value.toLowerCase().trim();
        setEmail(sanitizedEmail);

        // Clear previous suggestions and errors
        setEmailSuggestions([]);
        if (error) {
            setError('');
        }

        // Debounce email validation for suggestions
        if (typingTimer) {
            clearTimeout(typingTimer);
        }

        if (sanitizedEmail && sanitizedEmail.includes('@')) {
            const timer = setTimeout(() => {
                validateEmail(sanitizedEmail);
            }, 500);
            setTypingTimer(timer);
        }
    };

    const handleSuggestionClick = (suggestedEmail) => {
        setEmail(suggestedEmail);
        setEmailSuggestions([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear previous suggestions
        setEmailSuggestions([]);

        // Validate email format
        const validation = validateEmail(email);
        if (!validation.isValid) {
            setError(validation.error);
            return;
        }

        // Check for common email issues
        const emailIssues = checkEmailIssues(email);
        if (emailIssues.length > 0) {
            setError(`Email issues detected: ${emailIssues.join(', ')}`);
            return;
        }

        // Check rate limiting
        if (isRateLimited()) {
            setError(`Too many attempts. Please wait ${formatCooldown(cooldown)} before trying again.`);
            return;
        }

        setIsSubmitting(true);
        setError('');
        setMessage('');

        try {
            await requestPasswordReset(email);
            setMessage('If an account with that email exists, password reset instructions have been sent.');
            setEmail('');
            resetAttempts(); // Reset attempts on successful request
        } catch (err) {
            // Update attempt counter on failure
            updateAttempts();

            // Enhanced error handling with specific backend error mapping
            let errorMessage = err.message || 'Failed to send reset email. Please try again.';

            if (err.message.includes('network') || err.message.includes('Network')) {
                errorMessage = 'Network error. Please check your connection and try again.';
            } else if (err.message.includes('timeout')) {
                errorMessage = 'Request timeout. Please try again.';
            } else if (err.message.includes('not found') || err.message.includes('not exist')) {
                // Generic message for security - don't reveal if email exists
                errorMessage = 'If an account with that email exists, reset instructions have been sent.';
                setMessage('If an account with that email exists, reset instructions have been sent.');
            } else if (err.message.includes('not verified')) {
                errorMessage = 'Please verify your email address before resetting your password.';
            } else if (err.message.includes('too many requests')) {
                errorMessage = 'Too many reset attempts. Please wait before trying again.';
            }

            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Format cooldown time for display
    const formatCooldown = (minutes) => {
        if (minutes < 1) return 'less than a minute';
        return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    };

    return (
        <div className="h-screen bg-neutral flex flex-col justify-center px-4 sm:px-6 lg:px-8 font-sans">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="mt-6 text-center text-2xl font-extrabold text-primary mb-4 font-heading">
                    Reset Your Password
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 max-w-sm mx-auto font-body">
                    Enter your email address and we'll send you instructions to reset your password.
                </p>
            </div>

            <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-10 px-6 shadow rounded-2xl sm:px-12 border border-gray-50">
                    {/* Security Notice */}
                    {attempts > 0 && (
                        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                <span className="text-blue-700 font-medium font-body">Security Notice</span>
                            </div>
                            <p className="text-blue-600 text-sm mt-1 font-body">
                                {attempts} reset attempt{attempts > 1 ? 's' : ''} in the last hour.
                                {attempts >= 3 && ' Additional attempts may be limited.'}
                            </p>
                        </div>
                    )}

                    {/* Rate Limit Warning */}
                    {cooldown > 0 && (
                        <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                <span className="text-orange-700 font-medium font-body">Temporary Limit</span>
                            </div>
                            <p className="text-orange-600 text-sm mt-1 font-body">
                                Please wait {formatCooldown(cooldown)} before trying again.
                            </p>
                        </div>
                    )}

                    {message && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                <span className="text-green-700 font-medium font-body">Success</span>
                            </div>
                            <p className="text-green-600 text-sm mt-1 font-body">{message}</p>

                            {/* Additional instructions */}
                            <div className="mt-3 p-3 bg-green-100 rounded-lg">
                                <p className="text-green-800 text-xs font-body">
                                    <strong>What's next?</strong><br />
                                    • Check your inbox and spam folder<br />
                                    • Click the reset link in the email<br />
                                    • Create a new strong password<br />
                                    • The link expires in 1 hour
                                </p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                <span className="text-red-700 font-medium font-body">Error</span>
                            </div>
                            <p className="text-red-600 text-sm mt-1 font-body">{error}</p>
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1 font-body">
                                Email address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => handleEmailChange(e.target.value)}
                                placeholder="you@example.com"
                                disabled={cooldown > 0}
                                className={`block w-full border ${emailSuggestions.length > 0 ? 'border-blue-300' : 'border-gray-300'
                                    } rounded-lg shadow-sm py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-base font-body ${cooldown > 0 ? 'bg-gray-100 cursor-not-allowed' : ''
                                    }`}
                            />

                            {/* Email Suggestions */}
                            {emailSuggestions.length > 0 && (
                                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-xs text-blue-700 font-medium mb-2 font-body">
                                        Did you mean:
                                    </p>
                                    <div className="space-y-1">
                                        {emailSuggestions.map((suggestion, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => handleSuggestionClick(suggestion)}
                                                className="block w-full text-left text-xs text-blue-600 hover:text-blue-800 hover:underline font-body"
                                            >
                                                {suggestion}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <p className="mt-1 text-xs text-gray-500 font-body">
                                We'll send a secure reset link to this email address.
                            </p>
                        </div>

                        <div>
                            <Button
                                type="submit"
                                disabled={isSubmitting || cooldown > 0 || !email}
                                className="w-full py-3 px-4 text-base rounded-lg font-accent disabled:opacity-50 disabled:cursor-not-allowed"
                                variant="primary"
                            >
                                {isSubmitting ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </span>
                                ) : cooldown > 0 ? (
                                    `Try again in ${formatCooldown(cooldown)}`
                                ) : (
                                    'Send Reset Instructions'
                                )}
                            </Button>
                        </div>
                    </form>

                    {/* Help Section */}
                    <div className="mt-6 space-y-4">
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <h3 className="text-sm font-semibold text-gray-800 mb-2 font-body">Don't see the email?</h3>
                            <ul className="text-xs text-gray-600 space-y-1 font-body">
                                <li>• Check your spam or junk folder</li>
                                <li>• Wait a few minutes - delivery can take time</li>
                                <li>• Make sure your inbox isn't full</li>
                                <li>• Try requesting the reset link again</li>
                            </ul>
                        </div>
                    </div>

                    <div className="mt-8 text-center">
                        <Link
                            to="/login"
                            className="text-primary hover:text-primary-light font-medium text-sm hover:underline font-body"
                        >
                            ← Back to Sign In
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;